import { ReactNode, useState, useEffect } from 'react';

import { useClickAway } from '@uidotdev/usehooks';
import { Check, PenSquare } from 'lucide-react';

import AudioPlayer from './audio-player';
import { DiarizationCorrection } from './diarization-correction';
import { LogicalSpeaker } from '../hooks/useTranscription';
import { isLiveBlock, LiveTranscriptBlock } from '../types/transcriptTypes';
import { formatTimestamp } from '../utils/timeUtils';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { TranscriptBlock } from '@/api/types/backend-dto-types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { cn } from '@/new/lib/utils';

interface BaseTranscriptBlockProps {
  block: TranscriptBlock | LiveTranscriptBlock;
  caseId?: string;
  transcriptId?: string;
  isActive?: boolean;
  onClick?: () => void;
  onAcceptCorrection?: (correctionId: string, blockId: string) => void;
  onRejectCorrection?: (correctionId: string, blockId: string) => void;
  isProcessing?: boolean;
  openPopoverId?: string | null;
  onPopoverOpenChange?: (correctionId: string, open: boolean) => void;
  className?: string;
  children?: ReactNode;
  // Editing props
  speakers?: LogicalSpeaker[];
  onUpdate?: (blockId: string, content?: string, speakerId?: string | null) => void;
}

export function TranscriptBlockComponent({
  block,
  caseId,
  transcriptId,
  isActive = false,
  onClick,
  onAcceptCorrection,
  onRejectCorrection,
  isProcessing = false,
  children,
  speakers = [],
  onUpdate,
}: BaseTranscriptBlockProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(block.content);
  const [editedSpeakerId, setEditedSpeakerId] = useState<string | null>(block.internalSpeaker?.speaker?.id ?? null);
  // const textareaRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = useClickAway((event) => {
    if (isEditing) {
      // Don't cancel if clicking on a select dropdown or its portal content
      const target = event.target as Element;
      if (
        target.closest('[role="listbox"]') ||
        target.closest('[data-radix-popper-content-wrapper]') ||
        target.closest('[data-radix-select-content]') ||
        target.closest('button')
      ) {
        return;
      }

      handleCancelEdit();
    }
  }) as React.RefObject<HTMLTextAreaElement>;

  const internalSpeaker = block.internalSpeaker;
  const internalName = internalSpeaker?.internalName;

  const logicalSpeaker = internalSpeaker?.speaker ?? speakers?.find((s) => s.id === internalSpeaker?.speakerId);

  const displayName = logicalSpeaker?.name ?? internalName;
  const color = logicalSpeaker?.color ?? 'gray';

  // Check if this is a live block
  const isLive = isLiveBlock(block);
  const isPartial = isLive && block.isPartial;
  const audioReady = !isLive || block.audioReady !== false;

  // Reset edited values when block changes
  useEffect(() => {
    setEditedContent(block.content);
    setEditedSpeakerId(block.internalSpeaker?.speaker?.id ?? null);
  }, [block]);

  // Focus textarea when entering edit mode
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      const length = editedContent.length;
      textareaRef.current.setSelectionRange(length, length);
    }
  }, [isEditing]);

  const handleCancelEdit = () => {
    setEditedContent(block.content);
    setEditedSpeakerId(block.internalSpeaker?.speaker?.id ?? null);
    setIsEditing(false);
  };

  const handleContentEdit = (newContent: string) => {
    if (!onUpdate) return;

    const hasContentChanged = newContent !== block.content;

    if (!hasContentChanged) {
      setIsEditing(false);
      return;
    }

    trackCustomEvent(TrackingEventsV2.RealtimeEditBlockText);
    onUpdate(block.id, newContent, undefined);
    setIsEditing(false);
  };

  const handleSpeakerEdit = (newSpeakerId: string) => {
    if (!onUpdate) return;
    trackCustomEvent(TrackingEventsV2.RealtimeEditBlockSpeaker);
    onUpdate(block.id, undefined, newSpeakerId);
  };

  return (
    <div
      id={`block-${block.id}`}
      data-segment-id={block.id}
      className={cn([
        'relative border-l-2 p-5 pt-4',
        isActive
          ? '-translate-y-1 border-teal-300 bg-gradient-to-r from-teal-50 via-teal-50/20 via-5% to-transparent to-25%'
          : 'border-transparent',
        !isActive && !isPartial && 'hover:border-gray-200',
        onClick && 'cursor-pointer',
        'transition-all duration-200',
      ])}
      onClick={onClick}
    >
      {/* Top row: Speaker header, edit button, and timestamp */}
      <div className="mb-2 flex items-start justify-between">
        <div className="flex items-center gap-3">
          {/* Timestamp on the left */}
          <div className="min-w-[50px] text-xs text-gray-900/40">{formatTimestamp(block.timestamp)}</div>

          {/* Speaker name and audio player */}
          <div className="flex items-center gap-2">
            {/* Colored dot indicator for speaker */}
            <div
              className={`size-3 border-3 border-${color}-200 shrink-0 rounded-full bg-${color}-400 ring-1 ring-white`}
            />

            {!isPartial && transcriptId && caseId ? (
              <div data-audio-player>
                <AudioPlayer
                  blockId={block.id}
                  caseId={caseId}
                  transcriptId={transcriptId}
                  customColors={{
                    bg: 'bg-gray-100',
                    bgHover: 'hover:bg-gray-200',
                    text: 'text-gray-600',
                    border: 'border border-gray-300',
                  }}
                  className="h-5 w-5"
                  isProcessing={!audioReady}
                />
              </div>
            ) : null}

            {isPartial ? (
              <p className={`text-xs font-medium text-yellow-600`}>Active Speaker</p>
            ) : (
              <Select
                value={editedSpeakerId || ''}
                onValueChange={(value) => {
                  handleSpeakerEdit(value);
                }}
              >
                <SelectTrigger
                  className={`h-6 w-auto justify-start gap-2 border-0 bg-transparent px-0 text-xs font-medium text-${color}-500 shadow-none focus:ring-0`}
                >
                  {/* Use displayName as placeholder for internal-only speakers e.g. Guest 1 */}
                  <SelectValue placeholder={displayName} />
                </SelectTrigger>
                <SelectContent>
                  {speakers.map((speaker) => (
                    <SelectItem key={speaker.id} value={speaker.id}>
                      {speaker.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </div>

      {/* Content with inline diarization correction */}
      <div className="flex items-start gap-2 pt-1 pl-[62px]">
        {!isEditing ? (
          <>
            <div className={`flex-1 text-sm leading-relaxed ${isPartial ? 'text-gray-500 italic' : 'text-gray-700'}`}>
              {block.content}

              {!isPartial && (
                <span>
                  <button
                    className="ml-1.5 inline rounded-sm p-0.5 pb-0.75 text-gray-500 duration-150 hover:text-gray-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditing(true);
                    }}
                  >
                    <PenSquare size={12} className="-mb-0.25" />
                  </button>
                </span>
              )}
            </div>

            {/* Diarization correction - inline with content */}
            {block.correction && (
              <DiarizationCorrection
                correction={block.correction}
                speakers={speakers}
                onAccept={() => onAcceptCorrection?.(block.correction!.id, block.id)}
                onReject={() => onRejectCorrection?.(block.correction!.id, block.id)}
                isProcessing={isProcessing}
              />
            )}
          </>
        ) : (
          <div className="flex items-center gap-1">
            <textarea
              ref={textareaRef}
              className="field-sizing-content grow resize-none rounded-md border border-gray-200 p-2 text-sm transition-colors duration-150 focus:border-blue-300"
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  handleCancelEdit();
                  e.preventDefault();
                } else if (e.key === 'Enter' && !e.shiftKey) {
                  handleContentEdit(editedContent);
                  setIsEditing(false);
                  e.preventDefault();
                }
              }}
            />

            <button
              className="h-fit w-fit rounded-sm border border-gray-200 bg-gray-50 p-0.5 text-xs text-gray-600"
              onClick={() => handleContentEdit(editedContent)}
            >
              <Check size={14} />
            </button>
          </div>
        )}
      </div>

      {isPartial && <div className="mt-2 pl-[62px] text-xs text-yellow-600">(partial result)</div>}

      {children}
    </div>
  );
}
